import { useEffect, useState } from "react";
import { Users, MousePointerClick, Wallet, DollarSign, BadgeCheck, Mail, TrendingUp, Ban } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-emerald-600" />
      <p className="uppercase text-[10px] tracking-[0.15em] font-semibold text-slate-400">{label}</p>
    </div>
    <p className="font-heading text-3xl tracking-tight">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export const AdminOverview = ({ client, API }) => {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    client().get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => {});
    client().get(`${API}/admin/click-timeseries?days=14`).then((r) => setSeries(r.data.series)).catch(() => {});
  }, [client, API]);

  if (!stats) return <p className="text-sm text-slate-400 py-6">Loading stats…</p>;

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Ambassadors" value={stats.ambassadors.total} sub={`${stats.ambassadors.active} active${stats.ambassadors.suspended ? ` · ${stats.ambassadors.suspended} suspended` : ""}`} testId="stat-ambassadors" />
        <StatCard icon={MousePointerClick} label="Clicks today" value={stats.clicks.today} sub={`${stats.clicks.this_month} this month · ${stats.clicks.total} all time`} testId="stat-clicks" />
        <StatCard icon={Wallet} label="Pending payouts" value={`$${stats.payouts.pending_amount.toFixed(2)}`} sub={`${stats.payouts.pending_count} waiting to be paid`} testId="stat-pending-payouts" />
        <StatCard icon={DollarSign} label="Total paid out" value={`$${stats.payouts.paid_amount.toFixed(2)}`} sub={`${stats.payouts.paid_count} payouts completed`} testId="stat-paid-out" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Commissions owed" value={`$${stats.conversions.commissions_owed.toFixed(2)}`} sub={`${stats.conversions.count} conversions recorded`} testId="stat-commissions" />
        <StatCard icon={BadgeCheck} label="Featured revenue" value={`$${stats.featured.revenue}`} sub={`${stats.featured.paid_listings} paid listings ($49 each)`} testId="stat-featured-revenue" />
        <StatCard icon={Mail} label="Subscribers" value={stats.subscribers} sub="Newsletter signups" testId="stat-subscribers" />
        <StatCard icon={Ban} label="Suspended" value={stats.ambassadors.suspended} sub="Blocked from logging in" testId="stat-suspended" />
      </div>
      <div data-testid="clicks-chart-card" className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MousePointerClick className="w-4 h-4 text-emerald-600" />
          <p className="uppercase text-[10px] tracking-[0.15em] font-semibold text-slate-400">Daily clicks — last 14 days</p>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} labelFormatter={(d) => d} />
              <Area type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} fill="url(#clicksFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
